import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';

import { isFeatureEnabledForWarehouse, WarehouseFeatureEnum } from '@nova/core';
import { useLocalStorageRef } from '@satellite/composables/useLocalStorage';

import { toDateTime } from '@/composables/arrivals/arrivalDisplay';
import { useArrivalsDisplay } from '@/composables/arrivals/useArrivalsDisplay';
import { useRecentArrivalsFetcher } from '@/composables/arrivals/useRecentArrivalsFetcher';
import { useDialogTrigger } from '@/composables/useEventBasedDialog';

import type { MaybeRefOrGetter } from 'vue';
import type { Maybe } from '@nova/core';
import type { WarehouseDetails } from '@satellite/composables/useWarehousesPresenter';
import type { NotificationSource } from './types';

const LAST_SEEN_AT_STORAGE_KEY = 'notifications.arrivals.lastSeenAt';

export interface UseArrivalsNotificationSourceOptions {
  warehouse: MaybeRefOrGetter<Maybe<WarehouseDetails>>;
  userId: MaybeRefOrGetter<Maybe<string>>;
}

/**
 * Arrivals of the selected warehouse as a bell source. Call it from a component `setup`: it
 * listens to Subspace through the EventHub for as long as that component is mounted.
 */
export function useArrivalsNotificationSource(
  options: UseArrivalsNotificationSourceOptions
): NotificationSource {
  const { locale } = useI18n();
  const { triggerDialog } = useDialogTrigger('open-arrivals-drawer');

  const warehouse = computed(() => toValue(options.warehouse) ?? null);
  const warehouseId = computed(() => warehouse.value?.id ?? null);
  const hasGateManagement = computed(() =>
    isFeatureEnabledForWarehouse(warehouse.value, WarehouseFeatureEnum.GateManagement)
  );

  const { data: arrivals } = useRecentArrivalsFetcher({
    warehouseId,
    enabled: hasGateManagement
  });
  const arrivalsDisplay = useArrivalsDisplay(arrivals, {
    timezone: () => warehouse.value?.timezone,
    locale
  });

  // Per user and per warehouse: what one user has looked at says nothing about the others
  const lastSeenAtKey = computed(() => `${LAST_SEEN_AT_STORAGE_KEY}.${warehouseId.value}`);
  const { ref: lastSeenAt } = useLocalStorageRef<Maybe<string>>(lastSeenAtKey, null, {
    userId: () => toValue(options.userId) ?? undefined
  });

  const attentionCount = computed(() => arrivalsDisplay.attentionCount);

  // Only arrivals that still need someone count, so one already acknowledged by a teammate stays quiet
  const hasUnseen = computed(() => {
    const lastSeen = toDateTime(lastSeenAt.value);
    const arrivalsNeedingAttention = [
      ...arrivalsDisplay.pendingArrivals,
      ...arrivalsDisplay.plannedArrivals
    ].filter(arrival => !arrival.checkInAcknowledged);

    if (!lastSeen) {
      return arrivalsNeedingAttention.length > 0;
    }

    return arrivalsNeedingAttention.some(arrival => {
      const createdAt = toDateTime(arrival.createDateTime);
      return Boolean(createdAt && createdAt > lastSeen);
    });
  });

  const markSeen = () => {
    if (!warehouseId.value) {
      return;
    }

    lastSeenAt.value = DateTime.utc().toISO();
  };

  // Seen again on close, so arrivals that came in while the drawer was open do not animate
  const toggle = () => {
    markSeen();
    triggerDialog({ toggle: true, onClose: markSeen });
  };

  return {
    key: 'arrivals',
    attentionCount,
    hasUnseen,
    markSeen,
    toggle
  };
}
